import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  endOfDay,
  endOfMinute,
  endOfSecond,
  endOfMonth,
  endOfYear,
  startOfDay,
  startOfMinute,
  startOfSecond,
  startOfMonth,
  startOfYear,
  subMonths,
  subDays,
  subYears
} from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import React from 'react';
import { injectIntl } from 'react-intl';
import { lastDays, lastMonths, lastYears, THISMONTH, TODAY, YESTERDAY } from '../utils/ranges';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DatePickerIcon from '@mui/icons-material/DateRange';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import './DateRangePicker.css';
import differenceBy from 'lodash/differenceBy';

// TODO: Move the entire datepicker component to an external component which delivers to and from fields to this component.
class BSDatePicker extends React.Component {

  static propTypes = {
    dateRange: PropTypes.any,
    startIndex: PropTypes.any,
    callback: PropTypes.func,
    classes: PropTypes.any,
    withTime: PropTypes.bool,
    fullWidth: PropTypes.bool,
    label: PropTypes.object,
    hideArrow: PropTypes.bool,
    hideDateIcon: PropTypes.bool,
    allowClear: PropTypes.bool,
    intl: PropTypes.object,
    disabled: PropTypes.bool,
    autoload: PropTypes.bool,
    excludedOptions: PropTypes.array,
    hideToPicker: PropTypes.bool,
    preventDefault: PropTypes.bool
  };

  options = [
    {
      id: 'TODAY',
      label: this.props.intl.formatMessage({ id: 'range.today' }),
      value: TODAY,
      datePicker: {
        from: () => startOfDay(new Date()),
        to: () => endOfDay(new Date())
      }
    },
    {
      id: 'YESTERDAY',
      label: this.props.intl.formatMessage({ id: 'range.yesterday' }),
      value: YESTERDAY,
      datePicker: {
        from: () => subDays(startOfDay(new Date()), 1),
        to: () => subDays(endOfDay(new Date()), 1)
      }
    },
    {
      id: 'LAST7DAYS',
      label: this.props.intl.formatMessage({ id: 'range.lastDays' }, { days: 7 }),
      value: lastDays(7),
      datePicker: {
        from: () => subDays(startOfDay(new Date()), 7),
        to: () => endOfDay(new Date())
      }
    },
    {
      id: 'THISMONTH',
      label: this.props.intl.formatMessage({ id: 'range.thisMonth' }),
      value: THISMONTH,
      datePicker: {
        from: () => startOfMonth(new Date()),
        to: () => endOfMonth(new Date())
      }
    },
    {
      id: 'LASTMONTH',
      label: this.props.intl.formatMessage({ id: 'range.lastMonth' }),
      value: lastMonths(1),
      datePicker: {
        from: () => subMonths(startOfMonth(new Date()), 1),
        to: () => endOfMonth(subMonths(new Date(), 1))
      }
    },
    {
      id: 'LAST30DAYS',
      label: this.props.intl.formatMessage({ id: 'range.lastDays' }, { days: 30 }),
      value: lastDays(30),
      datePicker: {
        from: () => subDays(startOfDay(new Date()), 30),
        to: () => endOfDay(new Date())
      }
    },
    {
      id: 'THISYEAR',
      label: this.props.intl.formatMessage({ id: 'range.thisYear' }),
      value: lastYears(0),
      datePicker: {
        from: () => startOfYear(new Date()),
        to: () => endOfYear(new Date())
      }
    },
    {

      id: 'LASTYEAR',
      label: this.props.intl.formatMessage({ id: 'range.lastYear' }),
      value: lastYears(1),
      datePicker: {
        from: () => subYears(startOfYear(new Date()), 1),
        to: () => subYears(endOfYear(new Date()), 1)
      }
    },
    {

      id: 'UNRESTRICTED',
      label: this.props.intl.formatMessage({ id: 'range.unrestricted' }),
      value: lastYears(5),
      datePicker: {
        from: () => subYears(startOfYear(new Date()), 5),
        to: () => endOfYear(new Date())
      }
    },
    {
      id: 'PERIOD',
      label: this.props.intl.formatMessage({ id: 'range.period' })
    }
  ];

  constructor (props) {
    super(props);

    const optionsNew = differenceBy(this.options, this.props.excludedOptions, 'id');

    /* optionsNew[0].datePicker = {
      from: (Date.now() - (1000 * 60 * 5)),
      to: Date.now()
    }; */

    const selectedIndex = props.dateRange?.selectedIndex !== undefined && props.dateRange?.selectedIndex !== null ? props.dateRange?.selectedIndex : props.startIndex !== undefined ? (props.startIndex === -1 ? undefined : props.startIndex) : 0;
    const datePickerFrom = props.dateRange && props.dateRange.datePickerFrom ? props.dateRange.datePickerFrom : selectedIndex !== null && selectedIndex > -1 ? optionsNew[selectedIndex].datePicker?.from : undefined;
    const datePickerTo = props.dateRange && props.dateRange.datePickerTo ? props.dateRange.datePickerTo : selectedIndex !== null && selectedIndex > -1 ? optionsNew[selectedIndex].datePicker?.to : undefined;
    const periodDisabled = props.dateRange && props.dateRange.periodDisabled !== undefined ? props.dateRange.periodDisabled : true;

    const dateBoundary = new Date();

    this.state = {
      datePickerFrom,
      datePickerTo,
      selectedIndex: selectedIndex !== undefined ? selectedIndex : '',
      datePickerFromOpen: false,
      datePickerToOpen: false,
      periodDisabled,
      maxDateFrom: dateBoundary,
      minDateTo: undefined,
      hideDateIcon: props.hideDateIcon,
      hideArrow: props.hideArrow,
      allowClear: props.allowClear,
      autoload: props.autoload,
      options: optionsNew
    };

    this.anchorRef = React.createRef();
    this.clearValue = this.clearValue.bind(this);
  }

  componentDidMount () {
    if (this.state.selectedIndex === '') this.setState({ selectedIndex: undefined });
    const startIndex = this.state.selectedIndex ?? 0;
    if (this.state.selectedIndex !== undefined) {
      if (startIndex === (this.state.options.length - 1) && !this.props.preventDefault) {
        this.runTimeRangeQuery();
      } else if (this.state.autoload) {
        this.handleMenuItemClick(undefined, startIndex);
      }
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (JSON.stringify(this.props.dateRange) !== JSON.stringify(prevProps.dateRange)) {
      if (this.props.dateRange?.selectedIndex !== prevProps.dateRange?.selectedIndex) {
        this.handleMenuItemClick(undefined, this.props.dateRange?.selectedIndex);
      } else if (!this.props.dateRange?.datePickerFrom && !this.props.dateRange?.datePickerTo) {
        this.clearValue();
      }
    }
  }

  runTimeRangeQuery () {
    if (this.state.options.length === 1 && !this.state.datePickerTo && !this.props.hideToPicker) {
      this.handleOneDateRangeOption();
      // this.props.callback(this.state.datePickerFrom, this.state.datePickerTo, null, null);
    } else {
      const dateRange = {
        datePickerFrom: this.state.datePickerFrom,
        datePickerTo: this.state.datePickerTo,
        selectedIndex: this.state.options.length - 1
      };
      if ((this.isValidDate(this.state.datePickerFrom) && this.isValidDate(this.state.datePickerTo)) || this.props.preventDefault) {
        this.props.callback(this.state.datePickerFrom, this.state.datePickerTo, null, dateRange);
      } else {
        this.handleMenuItemClick(undefined, this.state.selectedIndex ?? 0);
      }
    }
  }

  isValidDate (date) {
    return date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date);
  }

  handleMenuItemClick = async (event, value) => {
    const index = value?.props?.value ?? value?.id ?? value;
    const range = index != null && this.state.options[index];

    if (range?.value) {
      // callback function with from and to values and appropriate interval for data.
      const dateRange = {
        selectedIndex: index,
        periodDisabled: true
      };
      this.props.callback(range.datePicker.from(), range.datePicker.to(), range.value.interval, dateRange, range.id);
      this.setState({ periodDisabled: true });
    } else if (range?.label) {
      this.setState({
        periodDisabled: false,
        ...(index !== this.state.selectedIndex && { datePickerFromOpen: true })
      });
    } else {
      this.clearValue();
    }

    this.setState({
      selectedIndex: index,
      selectedItem: range?.id
    });
  };

  onClose = () => {
    this.setState({ datePickerOpen: false });
  };

  handleAcceptValueFrom = (value) => {
    const { withTime } = this.props;
    // Open DatePickerTo when DatePickerFrom is closed
    this.setState({
      datePickerToOpen: true,
      minDateTo: value,
      datePickerFrom: withTime ? value : startOfDay(value)
    });
  };

  handleAcceptValueTo = (value) => {
    const { withTime } = this.props;

    this.setState({
      datePickerTo: withTime ? value : endOfDay(value)
    }, () => this.runTimeRangeQuery());
    // Clicking into the DateTimePicker inputs gives them focus, this releases that focus
    setTimeout(() => {
      document?.activeElement?.blur();
    }, 300);
  };

  handleKeyDown = (event, isFrom) => {
    if (event.key === 'Enter' || event.code === 'NumpadEnter') {
      this.runTimeRangeQuery();
    } else if (event.key === 'ArrowDown') {
      this.setState({
        datePickerFromOpen: isFrom,
        datePickerToOpen: !isFrom
      });
    }
  };

  clearValue () {
    const dateRange = {
      selectedIndex: null,
      periodDisabled: true
    };
    this.props.callback(null, null, null, dateRange);
    this.setState({
      datePickerFrom: null,
      datePickerTo: null,
      selectedIndex: null,
      periodDisabled: true
    });
  }

  handleOneDateRangeOption = () => {
    let dateRange;
    this.setState({
      periodDisabled: false,
      datePickerFrom: new Date(),
      datePickerTo: new Date(Date.now() + 60000)
    }, () => {
      dateRange = {
        datePickerFrom: this.state.datePickerFrom,
        datePickerTo: this.state.datePickerTo,
        selectedIndex: this.state.options.length - 1
      };
      if (this.isValidDate(this.state.datePickerFrom) && this.isValidDate(this.state.datePickerTo)) {
        this.props.callback(this.state.datePickerFrom, this.state.datePickerTo, null, dateRange);
      }
    }
    );
  };

  render () {
    const { withTime } = this.props;

    // const views = ['year', 'month', 'day', ...(withTime ? ['hours', 'minutes'] : [])];

    return (
      <div className={'dateRangePicker' + (withTime ? ' withTime' : '') }>
        <Autocomplete
          disablePortal
          disabled={this.props?.disabled}
          id="datePicker"
          name="datePicker"
          size='small'
          fullWidth={this.props.fullWidth}
          value={this.state.selectedIndex}
          onChange={this.handleMenuItemClick}
          spellCheck="false"
          disableClearable={!this.props.allowClear}
          disableListWrap
          selectOnFocus={false}
          isOptionEqualToValue={(option, value) => {
            if (typeof option === 'number') {
              return option === value;
            } else if (option?.label) {
              return option.id === value;
            } else {
              return false;
            }
          }}
          getOptionLabel={(option) => {
            if (option?.label) {
              return option.label;
            } else if (typeof option === 'number') {
              const status = this.state.options[option];
              return status?.label || '';
            } else {
              return '';
            }
          }}
          options={this.state.options.map((option, idx) => {
            return (
              { label: option.label, id: idx }
            );
          })}
          renderInput={(params) =>
            <TextField {...params}
              label={this.props.label}
              inputProps={{
                ...params?.inputProps,
                'aria-label': this.props.intl.formatMessage({ id: 'range.label' })
              }}
              InputProps={{
                ...params?.InputProps,
                startAdornment:
                !this.state.hideDateIcon
                  ? <InputAdornment position="start">
                    <DatePickerIcon />
                  </InputAdornment>
                  : undefined
              }}
            />}
        />

        <div className="range">
        {!this.state.periodDisabled &&
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={navigator.language.toLowerCase().startsWith('de') ? de : enUS}>
            <>
              <DateTimePicker
                size='small'
                disabled={this.props.disabled}
                withTime={withTime}
                disableFuture={true}
                onAccept={this.handleAcceptValueFrom}
                onChange={(date, keyboardInputValue) => {
                  const invalidDate = keyboardInputValue && keyboardInputValue.length < 16;
                  if (date && !invalidDate) {
                    this.setState({
                      datePickerFrom: startOfMinute(startOfSecond(date)),
                      minDateTo: startOfMinute(startOfSecond(date))
                    }, this.runTimeRangeQuery);
                  }
                }}
                onSelectedSectionsChange={(value, test, test2) => {
                  if (value === 'hours') {
                    this.setState({ datePickerFromOpen: false });
                    this.handleAcceptValueFrom(this.state.datePickerFrom);
                  }
                }}
                maxDate={this.state.maxDateFrom}
                value={this.state.datePickerFrom}
                showTodayButton={true}
                open={this.state.datePickerFromOpen}
                onClose={() => {
                  this.setState({ datePickerFromOpen: false });
                }}
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 20000
                    }
                  }
                }}
                onOpen={() => { this.setState({ datePickerFromOpen: true }); }}
                OpenPickerButtonProps={{ tabIndex: -1 }}
                renderInput={(params) => (
                  <TextField
                    {...params} style={{ width: '156px' }}
                    size="small"
                    onKeyDown={(event) => this.handleKeyDown(event, true)}
                   />
                )}
                showDaysOutsideCurrentMonth
                viewRenderers={{
                  hours: null,
                  minutes: null,
                  seconds: null
                }}
              />
              {
                !this.props.hideToPicker &&
                  <div className="separator">-</div>
              }
              {
                !this.props.hideToPicker &&
                  <DateTimePicker
                      disabled={this.props.disabled}
                      withTime={withTime}
                      onOpen={() => { this.setState({ datePickerToOpen: !this.state.datePickerToOpen }); }}
                      onChange={(date, keyboardInputValue) => {
                        const invalidDate = keyboardInputValue && keyboardInputValue.length < 16;
                        if (date && !invalidDate) { this.setState({ datePickerTo: endOfMinute(endOfSecond(date)) }, this.runTimeRangeQuery); }
                      }}
                      onClose={() => { this.setState({ datePickerToOpen: false }); }}
                      onSelectedSectionsChange={(value) => {
                        if (value === 'hours') {
                          this.setState({ datePickerToOpen: false });
                          this.handleAcceptValueTo(this.state.datePickerTo);
                        }
                      }}
                      slotProps={{
                        popper: {
                          sx: {
                            zIndex: 20000
                          }
                        }
                      }}
                      onAccept={this.handleAcceptValueTo}
                      maxDate={this.state.maxDate}
                      minDate={this.state.minDateTo}
                      value={this.state.datePickerTo}
                      open={this.state.datePickerToOpen}
                      showTodayButton={true}
                      OpenPickerButtonProps={{ tabIndex: -1 }}
                      renderInput={(params) => (
                          <TextField
                              {...params}
                              style={{ width: '156px' }}
                              size="small"
                              onKeyDown={(event) => this.handleKeyDown(event, false)}
                          />
                      )}
                      showDaysOutsideCurrentMonth
                      viewRenderers={{
                        hours: null,
                        minutes: null,
                        seconds: null
                      }}
                  />
              }
            </>
          </LocalizationProvider>
        }
        </div>
      </div>
    );
  }

}

export default injectIntl(BSDatePicker);
